import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useIntl } from "react-intl";
import AppGrid from "@lib/components/AppComponents/AppGrid";
import { useState, useEffect } from "react";
import { StyledContent, StyledResponsiveContainer } from "./index.styled";
import { Note } from "@lib/types/Note";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import NotesModal from "@lib/components/PageComponents/Notes/NotesModal";
import Filters from "./Filters";
import NoteItem from "./NoteItem";
import { Empty } from "antd";
import SearchBar from "./SearchBar";
import { useNoteQueries } from "@lib/apiQueries";

export default function NotesPage() {
  const { messages } = useIntl();
  const [activeKey, setActiveKey] = useState<string | null>();
  const [search, setSearch] = useState<string | null>();

  const { params, setParams } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    join: ["product", "invoice", "customer"],
  });

  const {
    notesQuery,
    addNoteMutation,
    updateNoteMutation,
    deleteNoteMutation,
  } = useNoteQueries({
    params,
    messages,
  });
  const { data: notes, isLoading, isFetching } = notesQuery;
  const apiData = notes?.data?.data;

  useEffect(() => {
    if (
      addNoteMutation.isSuccess ||
      updateNoteMutation.isSuccess ||
      deleteNoteMutation.isSuccess
    ) {
      toggle(false);
    }
  }, [
    addNoteMutation.isSuccess,
    updateNoteMutation.isSuccess,
    deleteNoteMutation.isSuccess,
  ]);

  const [openModal, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<Note>();

  useEffect(() => {
    if (!openModal) setSelected(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelect = (item: Note) => {
    setSelected(item);
    toggle(true);
  };

  const submitQuery = selected ? updateNoteMutation : addNoteMutation;
  const deleteQuery = deleteNoteMutation;

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  useEffect(() => {
    setParams({
      s: [
        {
          $and: [
            search && {
              text: {
                $cont: search?.trim(),
              },
            },
            activeKey && {
              [activeKey]: {
                $notnull: true,
              },
            },
          ].filter(Boolean),
        },
      ],
    });
  }, [search, activeKey]);

  return (
    <AppsContainer
      fullView={true}
      title={messages["common.notes"] as string}
      cardStyle={{
        boxShadow: "none",
      }}
    >
      <StyledResponsiveContainer>
        <Filters setActiveKey={setActiveKey} />

        <StyledContent>
          <SearchBar handleSearch={handleSearch} />

          <AppGrid
            responsive={{
              xs: 1,
              sm: 2,
              md: 2,
              lg: 3,
              xl: 3,
              xxl: 4,
            }}
            data={apiData}
            renderItem={(item, index) => (
              <NoteItem note={item} index={index} handleSelect={handleSelect} />
            )}
            loading={isLoading || isFetching}
            ListEmptyComponent={<Empty style={{ margin: "40px 0" }} />}
          />
        </StyledContent>
      </StyledResponsiveContainer>

      <NotesModal
        openModal={openModal}
        toggleModal={toggle}
        query={notesQuery}
        mutationQuery={submitQuery}
        deleteQuery={deleteQuery}
        selected={selected}
      />
    </AppsContainer>
  );
}
